import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CheckCircle2, XCircle, Loader2, Mail, Phone, Building2, Calendar,
  Filter, ExternalLink, ShieldCheck,
} from "lucide-react";

/**
 * Admin Approval Panel — turns a pending waitlist request into a working
 * login in one click. Approving mints a user (temp password), fires the
 * welcome email via Resend, and marks the request approved.
 */
const ROLE_OPTIONS = [
  { value: "cold_caller",          label: "Cold Caller" },
  { value: "lead_manager",         label: "Lead Manager" },
  { value: "acquisition_manager",  label: "Acquisition Manager" },
  { value: "admin",                label: "Admin (full access)" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString([], {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

export default function AdminAccessRequests() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen,  setRejectOpen]  = useState(false);
  const [target,      setTarget]      = useState(null);   // current request being acted on
  const [approveRole, setApproveRole] = useState("cold_caller");
  const [approveEmail, setApproveEmail] = useState(true);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectEmail,  setRejectEmail]  = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/access-requests");
      setRows(data.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load requests");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) =>
    tab === "all" ? true : (r.status || "pending") === tab
  );

  const counts = rows.reduce(
    (acc, r) => { acc[r.status || "pending"] = (acc[r.status || "pending"] || 0) + 1; return acc; },
    { pending: 0, approved: 0, rejected: 0 },
  );

  const openApprove = (req) => {
    setTarget(req);
    setApproveRole("cold_caller");
    setApproveEmail(true);
    setApproveOpen(true);
  };
  const openReject = (req) => {
    setTarget(req);
    setRejectReason("");
    setRejectEmail(false);
    setRejectOpen(true);
  };

  const doApprove = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/access-requests/${target.id}/approve`, {
        role: approveRole, send_email: approveEmail,
      });
      if (data.already_approved) {
        toast.info("Already approved earlier.");
      } else {
        toast.success(
          data.email_sent
            ? `Approved — welcome email sent to ${data.email}`
            : `Approved — user created (welcome email not sent)`
        );
      }
      setApproveOpen(false); setTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.post(`/admin/access-requests/${target.id}/reject`, {
        reason: rejectReason.trim() || null,
        send_email: rejectEmail,
      });
      toast.success("Request rejected");
      setRejectOpen(false); setTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="admin-access-requests">
      <header className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--app-text-muted)]">
            Waitlist
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
            <ShieldCheck size={22} /> Access Requests
          </h1>
          <p className="text-sm text-[var(--app-text-muted)] mt-1">
            Approve pending sign-ups. Approving instantly creates a login and
            emails the credentials via Resend.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}
                data-testid="requests-refresh-btn"
                className="rounded-full">
          <Filter size={13} className="mr-1.5" /> Refresh
        </Button>
      </header>

      <div className="flex gap-1 border-b border-[var(--app-line)] mb-4">
        {["pending", "approved", "rejected", "all"].map((k) => {
          const active = tab === k;
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 transition-colors ${
                active
                  ? "border-[var(--app-accent-dark)] text-[var(--app-text)]"
                  : "border-transparent text-[var(--app-text-muted)] hover:text-[var(--app-text)]"
              }`}
            >
              {k}
              {k !== "all" && (
                <span className="ml-1.5 text-[10px] opacity-70">
                  {counts[k] || 0}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="card-flat p-10 text-center text-sm text-[var(--app-text-muted)]">
          <Loader2 className="animate-spin mx-auto mb-2" size={18} /> Loading requests…
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-12 text-center" data-testid="requests-empty">
          <ShieldCheck size={28} className="mx-auto text-[var(--app-text-muted)] mb-3" />
          <div className="font-display text-lg font-bold">No {tab} requests</div>
          <p className="text-sm text-[var(--app-text-muted)] mt-1">
            {tab === "pending"
              ? "New applications will show up here as soon as they submit the form."
              : "Nothing to show for this filter yet."}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((r) => (
            <RequestCard
              key={r.id}
              req={r}
              onApprove={() => openApprove(r)}
              onReject={() => openReject(r)}
            />
          ))}
        </div>
      )}

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve {target?.name}</DialogTitle>
            <DialogDescription className="text-xs">
              A user will be created with a one-time temporary password. Resend
              will send the credentials to <b>{target?.email}</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Assign role</Label>
              <select
                value={approveRole}
                onChange={(e) => setApproveRole(e.target.value)}
                data-testid="approve-role-select"
                className="mt-1 h-9 w-full rounded-md border border-input bg-white px-2 text-sm"
              >
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={approveEmail}
                onChange={(e) => setApproveEmail(e.target.checked)}
                data-testid="approve-send-email-checkbox"
              />
              Send welcome email with temporary password
            </label>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setApproveOpen(false)}
                    data-testid="approve-cancel-btn">Cancel</Button>
            <Button
              onClick={doApprove}
              disabled={busy}
              data-testid="approve-confirm-btn"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {busy
                ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Approving…</>
                : <><CheckCircle2 size={13} className="mr-1.5" /> Approve & Create Login</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {target?.name}</DialogTitle>
            <DialogDescription className="text-xs">
              Mark this application as rejected. Optionally send a polite
              courtesy note.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Internal reason (optional)</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                data-testid="reject-reason-textarea"
                className="mt-1"
                placeholder="e.g. Not in target market, pilot at capacity, incomplete info"
              />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={rejectEmail}
                onChange={(e) => setRejectEmail(e.target.checked)}
                data-testid="reject-send-email-checkbox"
              />
              Send &quot;pilot is full&quot; courtesy email
            </label>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setRejectOpen(false)}
                    data-testid="reject-cancel-btn">Cancel</Button>
            <Button
              onClick={doReject}
              disabled={busy}
              data-testid="reject-confirm-btn"
              className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
            >
              {busy
                ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Rejecting…</>
                : <><XCircle size={13} className="mr-1.5" /> Reject</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RequestCard({ req, onApprove, onReject }) {
  const status = req.status || "pending";
  const badge = {
    pending:  { color: "bg-amber-100 text-amber-800 border-amber-300",   label: "Pending" },
    approved: { color: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Approved" },
    rejected: { color: "bg-rose-100 text-rose-800 border-rose-300",     label: "Rejected" },
  }[status];

  return (
    <div className="card-flat p-4 md:p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start"
         data-testid={`req-card-${req.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-lg font-bold" data-testid="req-name">
            {req.name || req.email}
          </div>
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${badge.color}`}
                data-testid="req-status-badge">
            {badge.label}
          </span>
          {req.role && (
            <span className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)]">
              · {req.role}
            </span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--app-text-muted)]">
          <span className="inline-flex items-center gap-1">
            <Mail size={11} /> <a href={`mailto:${req.email}`} className="hover:underline">{req.email}</a>
          </span>
          {req.phone && (
            <span className="inline-flex items-center gap-1">
              <Phone size={11} /> <a href={`tel:${req.phone}`} className="hover:underline">{req.phone}</a>
            </span>
          )}
          {req.company && (
            <span className="inline-flex items-center gap-1">
              <Building2 size={11} /> {req.company}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Calendar size={11} /> {fmtDate(req.created_at)}
          </span>
        </div>
        {(req.market || req.monthly_volume || req.referrer) && (
          <div className="mt-2 text-[11px] text-[var(--app-text-muted)] flex flex-wrap gap-2">
            {req.market          && <Pill k="Market"  v={req.market} />}
            {req.monthly_volume  && <Pill k="Volume"  v={req.monthly_volume} />}
            {req.referrer        && <Pill k="Source"  v={req.referrer} />}
          </div>
        )}
        {req.why && (
          <div className="mt-3 text-xs text-[var(--app-text)] bg-[var(--app-surface-2)] rounded-lg p-3 leading-relaxed"
               data-testid="req-why">
            <div className="text-[9px] uppercase tracking-widest text-[var(--app-text-muted)] mb-1">
              Why they applied
            </div>
            {req.why}
          </div>
        )}
        {status === "rejected" && req.reason && (
          <div className="mt-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            <span className="uppercase tracking-widest text-[9px] mr-1">Reason:</span>{req.reason}
          </div>
        )}
        {status === "approved" && req.assigned_role && (
          <div className="mt-2 text-[11px] text-emerald-800">
            Approved as <b>{req.assigned_role}</b> on {fmtDate(req.approved_at)}
          </div>
        )}
      </div>

      {status === "pending" && (
        <div className="flex md:flex-col gap-2 justify-end">
          <Button
            onClick={onApprove}
            data-testid={`approve-btn-${req.id}`}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4"
          >
            <CheckCircle2 size={13} className="mr-1.5" /> Approve
          </Button>
          <Button
            onClick={onReject}
            variant="outline"
            data-testid={`reject-btn-${req.id}`}
            className="rounded-full h-9 px-4 text-rose-700 hover:bg-rose-50 border-rose-300"
          >
            <XCircle size={13} className="mr-1.5" /> Reject
          </Button>
        </div>
      )}
      {status !== "pending" && (
        <a
          href={`mailto:${req.email}`}
          className="text-xs text-[var(--app-accent-dark)] hover:underline inline-flex items-center gap-1 whitespace-nowrap"
          data-testid={`mail-btn-${req.id}`}
        >
          <ExternalLink size={11} /> Contact
        </a>
      )}
    </div>
  );
}

function Pill({ k, v }) {
  return (
    <span className="inline-flex items-center gap-1 bg-[var(--app-surface-2)] rounded-full px-2 py-0.5">
      <span className="text-[9px] uppercase tracking-widest opacity-70">{k}</span>
      <span className="font-medium">{v}</span>
    </span>
  );
}
